import React, { useState, useEffect, useCallback, useRef } from "react";
import { Plus, Edit2, Users, Shield, User } from "lucide-react";
import UserModal from "./UserModal/UserModal";
import BulkEditModal from "./BulkEditModal/BulkEditModal";
import customFetch from "../../../helpers/axios";
import { normalizeApiResponse } from "../../../helpers/apiUtils";
import "./UserManagement.css";
import Combobox from "../../components/Combobox/Combobox";
import SearchInput from "../../components/SearchInput/SearchInput";
import UserStatCard from "../../components/UserStatCard/UserStatCard";
import "../../components/UserStatCard/UserStatCard.css";
import useUser from "../../../hooks/useUser";
import Button from "../../components/Buttons/Button/Button";
import Pagination from "../../components/Pagination/Pagination";
import "../../components/Pagination/Pagination.css";
import { useTranslation } from "react-i18next";

// Avoid module-scope flags so remounts always re-initialize correctly.

export default function UserManagement() {
  const { t, i18n } = useTranslation(['userManagement', 'common']);
  const [users, setUsers] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [creditLimits, setcreditLimits] = useState({});
  const { user } = useUser();
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [staffFilter, setStaffFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [canonicalPageSize, setCanonicalPageSize] = useState(0);
  const [maxSeenPageSize, setMaxSeenPageSize] = useState(0);

  // Bulk editing state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [statsCounts, setStatsCounts] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    admin: 0,
    student: 0,
  });
  // Refs to guard duplicate runs (e.g., React StrictMode) and memoize one-time fetches
  const initializedRef = useRef(false);
  const rolesLoadedRef = useRef(false);
  const filtersInitializedRef = useRef(false);
  const usersRequestRef = useRef({ url: null, inflight: false });
  const rolesRequestRef = useRef({ inflight: false });
  // Helper to compute stats from a given users array
  const updateStatsFromUsers = useCallback((list = [], totalOverride) => {
    const total = typeof totalOverride === 'number' ? totalOverride : list.length;
    setStatsCounts({
      total,
      active: list.filter(u => u.is_active).length,
      inactive: list.filter(u => !u.is_active).length,
      admin: list.filter(u => u.is_staff).length,
      student: list.filter(u => !u.is_staff).length,
    });
  }, []);
  // Fetch helpers defined at component scope so they can be used in handlers and effects
  const fetchUsers = useCallback(async (opts = {}) => {
    // Stable function: pass all variables explicitly to avoid re-creating this callback
    // opts: { url?, page?, search?, staff?, status? }
    try {
      const { url, page: pageArg = 1, search = "", staff = "all", status = "all" } = opts;
      let targetUrl = url;
      if (!targetUrl) {
        const base = "/api/users/";
        const params = new URLSearchParams();
        params.set("page", String(pageArg));
        if (search) params.set("search", search);
        if (staff !== "all") params.set("is_staff", staff === "admin" ? "true" : "false");
        if (status !== "all") params.set("is_active", status === "active" ? "true" : "false");
        const qs = params.toString();
        targetUrl = qs ? `${base}?${qs}` : base;
      }
      // Dedupe: if identical request is already in-flight, skip
      if (usersRequestRef.current.inflight && usersRequestRef.current.url === targetUrl) {
        return;
      }
      usersRequestRef.current = { url: targetUrl, inflight: true };
      const response = await customFetch.get(targetUrl);
      const normalized = normalizeApiResponse(response.data);
      setUsers(normalized.results);
      setPagination({
        count: normalized.count || 0,
        next: normalized.next || null,
        previous: normalized.previous || null,
      });
      // Update stats from the current page (approximate if server is paginated)
      updateStatsFromUsers(normalized.results || [], normalized.count);
      // Capture canonical and max seen page sizes using functional updates to avoid stale closures
      const len = normalized.results?.length || 0;
      if (len > 0) {
        setCanonicalPageSize(prev => {
          if (normalized.next && prev !== len) return len;
          if (!normalized.previous && prev === 0 && len > 0) return len;
          return prev;
        });
        setMaxSeenPageSize(prev => (len > prev ? len : prev));
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUsers([]);
      setPagination({ count: 0, next: null, previous: null });
    }
    finally {
      usersRequestRef.current.inflight = false;
    }
  }, [updateStatsFromUsers]);

  const fetchRolesAndcreditLimits = useCallback(async () => {
    try {
      if (rolesRequestRef.current.inflight) return;
      rolesRequestRef.current.inflight = true;
      const res = await customFetch.get(
        "/api/roles/",
        user && user.access
          ? { headers: { Authorization: `Bearer ${user.access}` } }
          : {}
      );
      const normalized = normalizeApiResponse(res.data);
      const data = normalized.results || [];

      const roles = data.map((role) => ({
        value: role.id,
        label: role.name,
      }));
      setRoleOptions(roles);

      const limits = {};
      data.forEach((role) => {
        limits[role.id] = role.credit_limit;
      });
      setcreditLimits(limits);
    } catch (error) {
      setRoleOptions([]);
      setcreditLimits({});
    } finally {
      rolesRequestRef.current.inflight = false;
    }
  }, [user]);

  // Removed separate stats fetch; stats are computed from the fetched users list

  useEffect(() => {
    // One-time initialization per mount; guard against StrictMode double-invoke in dev
    if (!(user && user.access)) return;
    (async () => {
      if (!rolesLoadedRef.current && roleOptions.length === 0) {
        rolesLoadedRef.current = true; // set guard before awaiting
        await fetchRolesAndcreditLimits();
      }
      if (!initializedRef.current) {
        setCanonicalPageSize(0);
        setMaxSeenPageSize(0);
        initializedRef.current = true; // set guard before awaiting
        await fetchUsers({ page: 1, search: "", staff: "all", status: "all" });
      }
    })();
  }, [user, roleOptions.length, fetchUsers, fetchRolesAndcreditLimits]);

  // Refetch when filters/search change; avoid re-fetch on initial mount and on page change here
  useEffect(() => {
    if (!(user && user.access)) return;
    if (!filtersInitializedRef.current) {
      // Skip the very first run to avoid duplicating the initial fetch
      filtersInitializedRef.current = true;
      return;
    }
    // Reset pagination-related trackers and go to page 1
    setPage(1);
    setCanonicalPageSize(0);
    setMaxSeenPageSize(0);
    fetchUsers({ page: 1, search: searchTerm, staff: staffFilter, status: statusFilter });
  }, [searchTerm, staffFilter, statusFilter, fetchUsers, user]);

  // Cambia el estado activo/inactivo de un usuario
  const handleToggleActive = async (userId, currentActive) => {
    try {
      const url = `/api/users/${userId}/`;
      const response = await customFetch.patch(url, {
        is_active: !currentActive,
      });
      const normalized = normalizeApiResponse(response.data);
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === userId ? normalized.results[0] : u))
      );
    } catch (error) {
      console.error("Error al actualizar usuario:", error);
    }
  };

  // Server-side filtering is applied via query params; no client-side filtering here

  // Reset to first page handled in the effect above that also fetches

  // Server-side pagination: compute indices without relying on a global pageSize
  let startIndex = 0;
  let endIndex = 0;
  if (users.length > 0) {
    if (pagination.next) {
      // Not last page: assume consistent size with current page
      startIndex = (page - 1) * users.length + 1;
    } else {
      // Last page: derive from total count and current page length
      startIndex = (pagination.count || 0) - users.length + 1;
    }
    endIndex = startIndex + users.length - 1;
  }
  const divisor = canonicalPageSize || maxSeenPageSize || (users.length || 1);
  const totalPages = Math.max(1, Math.ceil((pagination.count || 0) / divisor));
  const visibleUsers = users;

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };

  // PATCH: Editar usuario existente
  const handleSaveUser = async (userData) => {
    if (selectedUser) {
      try {
        const url = `/api/users/${selectedUser.id}/`;
        const response = await customFetch.patch(url, userData);
        const normalized = normalizeApiResponse(response.data);
        setUsers((prev) => {
          const updated = prev.map((u) =>
            u.id === selectedUser.id ? normalized.results[0] : u
          );
          // Update stats locally
          updateStatsFromUsers(updated, pagination.count || updated.length);
          return updated;
        });
      } catch (error) {
        console.error("Error actualizando usuario:", error);
        console.error("Error details:", error.response?.data);
        console.error("Error status:", error.response?.status);
        console.error("Error headers:", error.response?.headers);
      }
    } else {
      try {
        const response = await customFetch.post("/api/users/", userData);
        const normalized = normalizeApiResponse(response.data);
        setUsers((prev) => {
          const updated = [...prev, normalized.results[0]];
          updateStatsFromUsers(updated, (pagination.count || prev.length) + 1);
          return updated;
        });
      } catch (error) {
        console.error("Error creando usuario:", error);
        console.error("Error details:", error.response?.data);
        console.error("Error status:", error.response?.status);
        console.error("Error headers:", error.response?.headers);
      }
    }
  };

  // DELETE: Eliminar usuario
  const handleDeleteUser = async (userId) => {
    try {
      const url = `/api/users/${userId}/`;
      await customFetch.delete(url);
      setUsers((prev) => {
        const updated = prev.filter((u) => u.id !== userId);
        updateStatsFromUsers(updated, Math.max(0, (pagination.count || prev.length) - 1));
        return updated;
      });
    } catch (error) {
      console.error("Error eliminando usuario:", error);
    }
  };

  // Bulk editing functions
  const handleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === visibleUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(visibleUsers.map(user => user.id)));
    }
  };

  const handleBulkEdit = async (fieldUpdates) => {
    try {
      const userIds = Array.from(selectedUsers);
      await customFetch.patch('/api/users/bulk-field/', {
        ids: userIds,
        field_updates: fieldUpdates
      });

      // Refresh the data after bulk update
      await fetchUsers({ page, search: searchTerm, staff: staffFilter, status: statusFilter });
      setSelectedUsers(new Set());
      setIsBulkEditModalOpen(false);
    } catch (error) {
      console.error('Error performing bulk update:', error);
      throw error;
    }
  };

  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  return (
    <div className="user-mgmt-root">
      {/* Page Header */}
      <div className="user-mgmt-header">
        <div>
          <h1 className="user-mgmt-title">{t('header.title')}</h1>
          <p className="user-mgmt-subtitle">
            {t('header.subtitle')}
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="primary"
            onClick={() => {
              setSelectedUser(null);
              setIsUserModalOpen(true);
            }}
            leftIcon={<Plus size={16} />}
          >
            {t('header.newUser')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="user-mgmt-stats grid grid-cols-1 md:grid-cols-4 gap-4">
        <UserStatCard
          label={t('stats.total')}
          value={statsCounts.total}
          Icon={Users}
          iconColor="#039bff"
        />
        <UserStatCard
          label={t('stats.active')}
          value={statsCounts.active}
          Icon={Users}
          iconColor="#15803d"
        />
        <UserStatCard
          label={t('stats.inactive')}
          value={statsCounts.inactive}
          Icon={Users}
          iconColor="#b91c1c"
        />
        <UserStatCard
          label={t('stats.admins')}
          value={statsCounts.admin}
          Icon={Shield}
          iconColor="#6366f1"
        />
        <UserStatCard
          label={t('stats.students')}
          value={statsCounts.student}
          Icon={User}
          iconColor="#f59e42"
        />
      </div>

      {/* Filtros y buscador */}
      <div className="user-mgmt-table-section">
        <div className="user-mgmt-table-header">
          <div className="text-lg font-semibold">
            {t('header.title')}
          </div>

          <div className="user-mgmt-searchbar">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={t('filters.searchPlaceholder')}
              className="flex-1"
            />

            <Combobox
              value={staffFilter}
              onChange={setStaffFilter}
              options={[
                { value: "all", label: t('filters.roles.all') },
                { value: "admin", label: t('filters.roles.admin') },
                { value: "student", label: t('filters.roles.student') },
              ]}
              buttonClass="user-mgmt-filter-select"
            />

            <Combobox
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "all", label: t('filters.status.all') },
                { value: "active", label: t('filters.status.active') },
                { value: "inactive", label: t('filters.status.inactive') },
              ]}
              buttonClass="user-mgmt-filter-select"
            />
          </div>
        </div>

        {/* Bulk Actions Toolbar */}
        {selectedUsers.size > 0 && (
          <div className="selection-banner">
            <div className="selection-banner-content">
              <span className="selection-count">
                {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
              </span>
              <div className="selection-actions">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsBulkEditModalOpen(true)}
                  leftIcon={<Edit2 size={14} />}
                >
                  {i18n.t('userManagement:selectionMode.bulkEdit')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                >
                  {i18n.t('userManagement:selectionMode.clearSelection')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="card">
          <div className="card-content p-0">
            <div className="overflow-x-auto">
              <table className="user-table">
                <thead className="table-horizontal-lines">
                  <tr>
                    <th className="w-12">
                      <div className="user-mgmt-selectall">
                        <input
                          type="checkbox"
                          checked={selectedUsers.size === visibleUsers.length && visibleUsers.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300"
                        />
                        <span className="user-mgmt-selectall-label">{t('table.headers.selectAll')}</span>
                      </div>
                    </th>

                    <th>{t('table.headers.user')}</th>
                    <th>{t('table.headers.role')}</th>
                    <th>{t('table.headers.creditLimit')}</th>
                    <th>{t('table.headers.state')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.map((user, index) => {
                    const roleName =
                      roleOptions.find((opt) => opt.value === user.role_id?.id)
                        ?.label || user.role_id?.name || 'No Role';
                    return (
                      <tr
                        key={user.id}
                        className={`table-horizontal-lines row-border-muted ${selectedUsers.has(user.id) ? 'bg-blue-50' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleEditUser(user)}
                      >
                        <td className="w-12 user-mgmt-cell-checkbox" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(user.id)}
                            onChange={() => handleSelectUser(user.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="user-mgmt-cell-user">
                          <div className="flex items-center space-x-2">
                            <div className="owly-sidebar-avatar">
                              <span className="text-white font-medium">
                                {user.name.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="owly-user-management-name">
                                {user.name}
                              </p>
                              <p className="owly-user-management-email">
                                {user.email}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="user-mgmt-table-level user-mgmt-cell-role">
                          <span
                            className={`badge ${user.role_id?.have_admin_access
                                ? "filter-gold"
                                : (user.role_id?.id === 1 || user.role_id?.id === "1")
                                  ? "filter-blue"
                                  : (user.role_id?.id === 2 || user.role_id?.id === "2")
                                    ? "filter-green"
                                    : (user.role_id?.id === 3 || user.role_id?.id === "3")
                                      ? "filter-yellow"
                                      : (user.role_id?.id === 4 || user.role_id?.id === "4")
                                        ? "filter-purple"
                                        : "filter-blue"
                              }`}
                          >
                            {roleName}
                          </span>
                        </td>

                        <td className="user-mgmt-cell-credits">
                          <span className="th-waffle-flags">
                            {(() => {
                              // Use credit_limit from role_id object or fallback to creditLimits lookup
                              const creditLimit = user.role_id?.credit_limit ??
                                (creditLimits[user.role_id?.id] ||
                                  creditLimits[String(user.role_id?.id)]);
                              return creditLimit === null ? 'Unlimited' : (creditLimit?.toLocaleString() || 0);
                            })()} {" "}
                            {(() => {
                              const creditLimit = user.role_id?.credit_limit ??
                                (creditLimits[user.role_id?.id] ||
                                  creditLimits[String(user.role_id?.id)]);
                              return creditLimit !== null ? t('table.perDay') : '';
                            })()}
                          </span>
                        </td>

                        <td className="user-mgmt-cell-state" onClick={(e) => e.stopPropagation()}>
                          <button
                            className={`user-mgmt-table-status ${user.is_active ? "active" : "inactive"
                              }`}
                            title={user.is_active ? t('table.deactivateTitle') : t('table.activateTitle')}
                            onClick={() =>
                              handleToggleActive(user.id, user.is_active)
                            }
                          >
                            {user.is_active ? t('table.active') : t('table.inactive')}
                          </button>
                        </td>
                        {/* Removed Actions cell */}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          </div>

          {/* Pagination */}
          {visibleUsers.length > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={async (next) => {
                await fetchUsers({ page: next, search: searchTerm, staff: staffFilter, status: statusFilter });
                setPage(next);
              }}
              infoBelow={
                <span className="usage-page-info">
                  {t('pagination.summary', { start: (visibleUsers.length === 0 ? 0 : startIndex), end: endIndex, total: (pagination.count || visibleUsers.length) })}
                </span>
              }
            />
          )}

        
      </div>

      {visibleUsers.length === 0 && (
        <div className="text-center py-8">
          <Users size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('empty.title')}
          </h3>
          <p className="text-gray-600">
            {t('empty.subtitle')}
          </p>
        </div>
      )}

      {/* User Modal */}
      <UserModal
        isOpen={isUserModalOpen}
        onDelete={handleDeleteUser}
        onClose={() => {
          setIsUserModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSave={handleSaveUser}
        users={users}
        creditLimits={creditLimits}
        roleOptions={roleOptions} // Pasar roleOptions como prop
      />

      {/* Bulk Edit Modal */}
      <BulkEditModal
        isOpen={isBulkEditModalOpen}
        onClose={() => setIsBulkEditModalOpen(false)}
        onSave={handleBulkEdit}
        selectedCount={selectedUsers.size}
        roleOptions={roleOptions}
      />
    </div>
  );
}
